<template>
    <button
        ref="rootElement"
        v-qa="country.isoCode"
        :aria-selected="selected"
        :class="{ selected }"
        class="country-option focusable"
        role="option"
        type="button"
        @click="emit('select', country)"
        @keydown.enter.prevent.stop="emit('select', country)"
    >
        <Flag :country="country.isoCode" />

        <span class="name">{{ countryLabel }}</span>

        <Icon
            v-if="selected"
            class="checkmark"
            icon="checkmark"
        />
    </button>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue'

import Flag from '@/components/Flag.vue'
import Icon from '@/components/ui/icon/Icon.vue'
import { useTranslator } from '@/i18n'
import type { ShippingCountryFragment } from '@/graphql/api'

const { country, selected } = defineProps<{
    country: ShippingCountryFragment['countries'][number]
    selected: boolean
}>()

const emit = defineEmits<{
    select: [country: ShippingCountryFragment['countries'][number]]
}>()

const { getCountryName } = useTranslator()

const countryLabel = computed((): string => getCountryName(country.isoCode))

const rootElement = useTemplateRef<HTMLButtonElement>('rootElement')

defineExpose({
    rootElement,
})
</script>

<style lang="scss" scoped>
.country-option {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 44px;
    padding: 10px 12px;
    border: 1px solid transparent;
    border-radius: var(--border-radius-m, 8px);
    background: transparent;
    text-align: left;
    font-family: $effra-font-stack;
    color: $slate;
    cursor: pointer;
    @include default-transition();

    @include min-width(1024) {
        min-height: 48px;
        padding: 12px 14px;
    }

    &.focusable {
        &:focus {
            outline: none;
        }

        &:focus-visible {
            border-color: $blue;
            box-shadow: 0 0 0 1px $blue;
        }

        @include hover() {
            background: $stone-2;
        }
    }

    &.selected {
        background: $stone-2;
        border-color: $grey-300;
    }

    .flag {
        flex-shrink: 0;
        font-size: 1.125rem;
        line-height: 1;
    }

    .name {
        flex: 1 1 auto;
        min-width: 0;
        font-size: 0.875rem;
        line-height: 1.3;

        @include min-width(1024) {
            font-size: 0.9375rem;
        }
    }

    .currency {
        flex-shrink: 0;
        font-size: 0.75rem;
        font-weight: 400;
        letter-spacing: 0.02em;
        color: $grey-500;
        white-space: nowrap;
    }

    .checkmark {
        flex-shrink: 0;
        font-size: 12px;
        color: $blue;
    }
}
</style>
